import { useState, useCallback, useEffect } from "react"
import { useParams } from "react-router-dom"
import { Trans, useTranslation } from "react-i18next"
import { downloadExport } from "../../../helpers/downloadExport"
import { Badge, Group, Text, ActionIcon, TextInput, Select, Button, Tooltip, Card } from "@mantine/core"
import { modals } from "@mantine/modals"
import { DataTable, useDataTable } from "../../../components/DataTable"
import type { Column } from "../../../components/DataTable"
import { useDeleteContact, useContactTypes } from "../hooks/useContacts"
import { ContactSchema } from "../models/contact"
import type { Contact } from "../models/contact"
import { useUser } from "../../user/hooks/useUser"
import { PageLayout, PageHeader } from "../../../components/crud"
import ContactDrawer from "./ContactDrawer"
import type { DrawerMode } from "./ContactDrawer"

export default function ContactList() {
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageContacts ?? false
  const params = useParams<{ "*"?: string }>()
  const routeId = params["*"] ? Number(params["*"].split("/")[0]) : NaN

  const { data, isLoading, page, pageSize, sort, sortDir, handleSortChange, handlePageChange, filters, updateFilters } =
    useDataTable<Contact>({
      queryKey: "contacts",
      url: "contact",
      schema: ContactSchema,
      defaultSort: { field: "id", dir: "desc" },
      defaultPageSize: 50,
    })

  const { data: typesData } = useContactTypes()
  const deleteMutation = useDeleteContact()

  const [drawerOpened, setDrawerOpened] = useState(false)
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("view")
  const [selectedContactId, setSelectedContactId] = useState<number | null>(null)

  const openDrawer = useCallback((id: number | null, mode: DrawerMode) => {
    setSelectedContactId(id)
    setDrawerMode(mode)
    setDrawerOpened(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setDrawerOpened(false)
    setSelectedContactId(null)
  }, [])

  const handleDeleted = useCallback(() => {
    closeDrawer()
  }, [closeDrawer])

  useEffect(() => {
    if (!Number.isNaN(routeId) && routeId > 0) {
      openDrawer(routeId, "view")
    }
  }, [routeId, openDrawer])

  const handleDelete = (id: number) => {
    modals.openConfirmModal({
      title: t("contacts.confirmDelete"),
      children: <Text size="sm">{t("contacts.confirmDeleteMessage")}</Text>,
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => deleteMutation.mutate(id),
    })
  }

  const typeOptions = (typesData?.contact_types ?? []).map((t: { id: number; option: string }) => ({
    value: String(t.id),
    label: t.option,
  }))

  const columns: Column<Contact>[] = [
    {
      key: "name",
      label: t("contacts.name"),
      sortable: true,
      render: (v) => <Text fw={500}>{v.name}</Text>,
    },
    {
      key: "email",
      label: t("contacts.email"),
      sortable: true,
      render: (v) => v.email || "-",
    },
    {
      key: "phone_number",
      label: t("contacts.phone"),
      sortable: true,
      width: 130,
    },
    {
      key: "type",
      label: t("contacts.type"),
      sortable: true,
      width: 150,
      render: (v) => <Badge variant="light" size="sm">{v.type_name}</Badge>,
    },
  ]

  return (
    <PageLayout
      header={
        <PageHeader
          title={t("contacts.title")}
          actions={
            <Group>
              <Button
                variant="default"
                onClick={() => downloadExport("contact/export", "contactos.xlsx")}
                leftSection={<i className="fa fa-file-excel-o" />}
              >
                <Trans i18nKey="common.export" />
              </Button>
              {canManage && (
                <Button onClick={() => openDrawer(null, "create")} leftSection={<i className="fa fa-plus" />}>
                  <Trans i18nKey="contacts.create" />
                </Button>
              )}
            </Group>
          }
        />
      }
    >
      <Card withBorder p="sm" radius="md">
        <Group grow align="flex-end">
          <TextInput
            label={t("contacts.filterName")}
            placeholder={t("contacts.searchName")}
            onChange={(e) => updateFilters({ name: e.currentTarget.value })}
          />
          <TextInput
            label={t("contacts.email")}
            placeholder={t("contacts.searchEmail")}
            onChange={(e) => updateFilters({ email: e.currentTarget.value })}
          />
          <Select
            label={t("contacts.filterType")}
            placeholder={t("contacts.filterType")}
            data={typeOptions}
            clearable
            onChange={(val) => updateFilters({ type: val })}
          />
        </Group>
      </Card>

      <DataTable<Contact>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        sort={sort}
        sortDir={sortDir}
        loading={isLoading}
        emptyMessage={t("contacts.empty")}
        keyExtractor={(v) => v.id}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onRowClick={(v) => openDrawer(v.id, "view")}
        actions={(v) => (
          <Group gap={4} justify="flex-end" wrap="nowrap">
            {canManage && (
              <>
                <Tooltip label={t("common.edit")}>
                  <ActionIcon variant="subtle" size="md" onClick={() => openDrawer(v.id, "edit")}>
                    <i className="fa fa-pencil" />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label={t("common.delete")}>
                  <ActionIcon variant="subtle" size="md" color="red" onClick={() => handleDelete(v.id)}>
                    <i className="fa fa-trash" />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Group>
        )}
      />

      <ContactDrawer
        opened={drawerOpened}
        onClose={closeDrawer}
        mode={drawerMode}
        selectedContactId={selectedContactId}
        onDeleted={handleDeleted}
        onSwitchToEdit={() => setDrawerMode("edit")}
      />
    </PageLayout>
  )
}
